import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';

import { FeedPhotoCardDetails } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import challengeData from '../helpers/state/challenge-data';
import { isDefined } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';

@customElement('photo-feed-card')
export class PhotoFeedCard extends LitElement {
    static amplitude_callsite: string = 'Photo Feed Card';

    @property({ type: String })
    pillText?: string;

    @state()
    photoDetails?: FeedPhotoCardDetails;

    @state()
    imageUrl: string = '';

    private readonly bindTopicState = new StateController(this, topicData);
    private observer?: IntersectionObserver;
    private isIntersecting = false;

    connectedCallback() {
        super.connectedCallback();

        this.observer = new IntersectionObserver(([entry]) => {
            this.isIntersecting = entry.isIntersecting;

            if(this.isIntersecting) {
                // Attempt to load images when the card comes into view.
                // The images may already be loaded, but the `loadImages`
                // function will check that.
                this.loadImages().catch((error) => {
                    console.error('Error loading images:', error);
                });
            }
        });

        this.observer.observe(this);
      }

    disconnectedCallback() {
        this.observer?.disconnect();
        super.disconnectedCallback();
    }

    protected async updated(changedProperties: Map<string | number | symbol, unknown>) {
        super.updated(changedProperties);

        if (changedProperties.has('photoDetails')) {
            // Clear the images when the point changes,
            // so that we attempt not to display incorrect images.
            this.imageUrl = '';

            // Attempt to load images (may not if card is not visible).
            this.loadImages().catch((error) => {
                console.error('Error loading images:', error);
            });
        }
    }

    protected async loadImages() {
        // Bail if we do not have a photoDetails object.
        if (!isDefined(this.photoDetails)) {
            return;
        }

        // Bail if this card is not in view.
        if (!this.isIntersecting) {
            return;
        }

        // Bail if we already have images.
        if (this.imageUrl.length > 0) {
            return;
        }

        // Cache the _current_ point id.
        // We need to do this because this element is sometimes virtualized, and
        // reused for different points.  So, we should check that the point has not
        // changed before we set the images.
        const currentActivatedChallengeId = this.photoDetails?.activated_challenge_id.$oid;

        // Get the images.
        const imageUrlFinal = this.photoDetails?.image_url;

        // Bail if the point has changed since we started loading them.
        if (this.photoDetails?.activated_challenge_id.$oid !== currentActivatedChallengeId) {
            return;
        }

        // Now we can set the images and continue.
        this.imageUrl = imageUrlFinal;
    }

    render() {
        if (!this.photoDetails) {
            return html`<div class="mini-point-card photo-card--empty">Loading...</div>`;
        }

        const formattedDate = this.photoDetails.captured_at ?
            new Date(this.photoDetails.captured_at).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                        }) : '';

            return html`
                    <div class="photo-card">
                        <div class="photo-card__header">
                            <div class="photo-card__user">
                                <div class="photo-card__user-info">
                                    <mwc-icon class="photo-card__user-icon">account_circle</mwc-icon>
                                    <div class="photo-card__username">${this.photoDetails.username}</div>
                                </div>
                            </div>
                        </div>
                        <div class="photo-card__image-container">
                            ${this.imageUrl ?
                                html`<img class="photo-card__image" src="${this.imageUrl}" alt="User photo" @load="${this.handleImageLoad}">` :
                                html`<div class="photo-card__image-placeholder">
                                    <mwc-icon>image</mwc-icon>
                                </div>`
                            }
                        </div>

                        <div class="photo-card__footer">
                            <div class="photo-card__location-name">
                                <mwc-icon class="location-icon">place</mwc-icon>
                                <span>${this.locationOrActivityName}</span>
                            </div>
                            <div class="photo-card__date">
                                <mwc-icon class="date-icon">calendar_today</mwc-icon>
                                <span>${formattedDate}</span>
                            </div>
                        </div>
                    </div>
                `;
                }

    // Add this method to get the location name
    get locationOrActivityName(): string {
        if (!this.photoDetails) return 'Unknown location';

        const locationId = this.photoDetails.location_or_activity_id;

        const location = challengeData.challengeLocations.find(
            location => location.challenge_location_info?._id?.$oid === locationId.$oid
        );

        if (!location) {
            const activity = challengeData.challenge?.activities?.find(
                activity => activity._id?.$oid === locationId.$oid
            );
            if (activity) {
                return activity.name || 'Unknown activity';
            }
            return 'Unknown location';
        } else {
            return location.point_info?.name ?? 'Unknown location';
        }
    }

    // Handle image loading to adjust layout based on image dimensions
    private handleImageLoad(e: Event) {
        const img = e.target as HTMLImageElement;
        const aspectRatio = img.naturalWidth / img.naturalHeight;

        if (aspectRatio > 1) {
            img.classList.add('photo-card__image--horizontal');
        } else {
            img.classList.add('photo-card__image--vertical');
        }
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 36px;
                --mdc-typography-button-letter-spacing: 0px;
            }

            .mini-point-card {
                word-wrap: break-word;
                border-radius: 4px;
                display: flex;
                height: 80px;
                cursor: pointer;
                overflow-y: auto;
                box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
                gap: 8px;
            }

            .mini-point-card__content_image {
                padding: 6px 16px 6px 8px;
                width: 60%;
                background: var(--theme-white-color);
                overflow: hidden;
                opacity: 0.98;
                flex: 1;
                padding-left: 3px;
                display: flex;
                flex-direction: column;
                gap: 4px;
            }

            .mini-point-card__content_no_image {
                padding: 12px 16px;
                width: 100%;
                background: white;
                overflow: hidden;
                opacity: 0.98;
            }

            .mini-point-card__image {
                width: 100%;
                height: 100%;
                object-fit: cover;
                opacity: 1;
            }

            .mini-point-card__icon {
                height: 75%;
                width: auto;
                opacity: 1;
                background-color: var(--theme-border-color);
            }

            .mini-point-card__point-number {
                margin-right: 2px;
                z-index: 1;
            }

            .mini-point-card__description {
                color: var(--theme-lighter-text-color);
                display: block;
                margin-bottom: 8px;
                overflow: hidden;
                text-overflow: ellipsis;
                height: 55px;
            }

            .mini-point-card__cover {
                height: 40px;
                background: linear-gradient(to top, white, transparent 100%);
                margin-top: -50px;
                padding: 5px;
                width: 100%;
                margin-left: -5px;
                position: relative;
            }

            .mini-point-card__category-wrapper {
                display: flex;
                gap: 5px;
                white-space: nowrap;
                flex-wrap: wrap;
            }

            .mini-point-card__category-pill {
                border-radius: 20px;
                font-size: 12px;
                padding: 2px 6px;
                gap: 3px;
                display: flex;
                align-items: center;
                border: 1px solid var(--theme-border-color);
            }

            .photo-card__image {
                max-width: 100%;
                overflow: hidden;
                position: relative;
            }

            .photo-card {
                display: flex;
                flex-direction: column;
                background: white;
                padding: 10px;
                margin: 10px 0;
                box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;
            }

            .mini-point-card__image-wrapper {
                display: flex;
                width: 25%;
                background-color: var(--theme-white-color);
                overflow: hidden;
                align-items: center;
                opacity: 0.98;
            }

            .wrapper-icon {
                background-color: var(--theme-border-color);
                justify-content: center;
            }

            .space-wrapper {
                flex: 1;
                background-color: var(--theme-white-color);
            }

            .mini-point-card__title-text {
                font-weight: 700;
                font-size: 14px;
                color: var(--main-primary-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__address {
                color: var(--theme-lighter-text-color);
                display: block;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__event {
                color: var(--theme-lighter-text-color);
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
            }

            .mini-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 20px;
                width: 16px;
                padding-right: 4px;
            }

            .mini-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .mini-point-card__title {
                display: flex;
            }

            .photo-card__footer {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-top: 10px;
            }

            .photo-card__date {
                display: flex;
                align-items: center;
                --mdc-icon-size: 12px;
                gap: 4px;
            }

            .photo-card__location-name {
                display: flex;
                align-items: center;
                --mdc-icon-size: 12px;
                gap: 4px;
            }

            .photo-card__user-info {
                display: flex;
                align-items: center;
                --mdc-icon-size: 14px;
                font-weight: 500;
                font-size: 16px;
                gap: 4px;
                border-bottom: 1px solid var(--theme-border-color);
                margin-bottom: 5px;
            }

            .deselect-control {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 1rem;
                gap: 0.25rem;
                cursor: pointer;
                background-color: var(--theme-opacity-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 2rem;
                font-size: 14px;
            }

            @media (max-width: 600px) {
                .mini-point-card {
                    height: 100px;
                }

                .mini-point-card__content_image {
                    padding: 6px 16px 6px 8px;
                }
            }
        `;
    }
}